// Payments.js - UI matched with OrderstatusImage

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FaSearch,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckDouble,
  FaEllipsisV,
  FaMoneyBillWave,
  FaDollarSign,
  FaCreditCard,
  FaShoppingCart,
  FaChevronDown,
  FaStore,
  FaCity,
  FaCheck,
  FaBuilding,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { RiDeleteBinFill } from 'react-icons/ri';
import { IoMdArrowDropdown } from 'react-icons/io';
import { SiAlipay } from 'react-icons/si';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';
import './Payments.css';

// Saved copies (per shop) so the page shows data instantly on the next visit
// while fresh data loads in the background.
const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const paymentsKey = (id) => `payments_${id}`;
const citiesKey = (id) => `payments_cities_${id}`;

const Payments = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    formatPrice,
    currency,
    loading: currencyLoading,
    getCurrencySymbol
  } = useCurrency();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly.
  const startShopId = shopId && shopId !== 'all' ? shopId : null;
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cachedPayments = startShopId ? readJson(paymentsKey(startShopId)) : null;
    const cachedCities = startShopId ? readJson(citiesKey(startShopId)) : null;
    initialRef.current = {
      payments: Array.isArray(cachedPayments) ? cachedPayments : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);

  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentPaid, setPaymentPaid] = useState('');

  // ✅ NEW: Order fields for edit
  const [subtotal, setSubtotal] = useState('');
  const [vatAmount, setVatAmount] = useState('');
  const [deliveryCharges, setDeliveryCharges] = useState('');
  const [discount, setDiscount] = useState('');
  const [orderTotal, setOrderTotal] = useState('');
  const [applyVat, setApplyVat] = useState(false);
  const [applyDeliveryCharges, setApplyDeliveryCharges] = useState(false);

  const [order, setOrder] = useState('');
  const [orderId, setOrderId] = useState('');
  const [selectedOrderTrackingId, setSelectedOrderTrackingId] = useState('');

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0
  });

  const [payments, setPayments] = useState(initialRef.current.payments || []);
  // True only while there is nothing to show yet (so "No payments found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.payments);
  // loading = a save / delete is running (disables the buttons)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [shopName, setShopName] = useState('');
  const [shops, setShops] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [fetchingOrders, setFetchingOrders] = useState(false);

  const [selectedShopForPayment, setSelectedShopForPayment] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [ukShopId, setUkShopId] = useState(null);
  const [isUkShopSet, setIsUkShopSet] = useState(false);

  // ✅ Header filter cities
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [selectedCity, setSelectedCity] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [loadingCities, setLoadingCities] = useState(false);

  // ✅ Header filter cities (dedicated)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);

  const orderInputRef = useRef(null);
  const orderDropdownRef = useRef(null);

  const paymentsRequestRef = useRef(0);
  const shopsFetchedRef = useRef(false);
  const prevShopRef = useRef(startShopId);
  const citiesLoadedForRef = useRef(null);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

  const paymentStatusOptions = ['Pending', 'Paid', 'Failed', 'Refunded', 'Partial'];

  // ✅ City counts are calculated from the loaded payments
  const cityCounts = useMemo(() => {
    const counts = {};
    payments.forEach(p => {
      if (p.city) {
        counts[p.city] = (counts[p.city] || 0) + 1;
      }
    });
    return counts;
  }, [payments]);

  const extractNumericValue = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const numericString = String(value).replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? 0 : parsed;
  };

  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#3085d6'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  const showDeleteConfirmation = (paymentName, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} payment(s)?`
      : `Are you sure you want to delete payment for "${paymentName}"?`;

    return Swal.fire({
      title: 'Are you sure?',
      text: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const fetchCitiesForShop = async (shopIdValue) => {
    if (!shopIdValue || shopIdValue === 'all') {
      setCities([]);
      setSelectedCity('');
      return;
    }
    setLoadingCities(true);
    try {
      const response = await fetch(`${API_BASE_URL}/order/shop-cities/?shop_id=${shopIdValue}`);
      if (response.ok) {
        const data = await response.json();
        setCities(data.cities || []);
        if (data.cities && data.cities.length > 0) {
          setSelectedCity(data.cities[0] || '');
        } else {
          setSelectedCity('');
        }
      } else {
        setCities([]);
        setSelectedCity('');
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
      setSelectedCity('');
    } finally {
      setLoadingCities(false);
    }
  };

  // ✅ Fetch cities for header filter
  const fetchFilterCities = async (shopIdValue) => {
    if (!shopIdValue || shopIdValue === 'all') {
      setFilterCities([]);
      setSelectedFilterCity('');
      return;
    }
    // The dropdown only shows "Loading..." when there is no saved list yet
    const hasSavedCities = Array.isArray(readJson(citiesKey(shopIdValue)));
    if (!hasSavedCities) setLoadingFilterCities(true);
    try {
      const response = await fetch(`${API_BASE_URL}/order/shop-cities/?shop_id=${shopIdValue}`);
      if (response.ok) {
        const data = await response.json();
        const citiesData = data.cities || [];
        setFilterCities(citiesData);
        setSelectedFilterCity('');
        writeJson(citiesKey(shopIdValue), citiesData);
      } else {
        setFilterCities([]);
        setSelectedFilterCity('');
      }
    } catch (error) {
      console.error('Error fetching filter cities:', error);
      setFilterCities([]);
      setSelectedFilterCity('');
    } finally {
      setLoadingFilterCities(false);
    }
  };

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  const findUkShop = (shopsList) => {
    const ukShop = shopsList.find(shop => shop.shop_name && shop.shop_name.toLowerCase().includes('uk'));
    return ukShop || shopsList[0];
  };

  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/order/shops/`);
      if (response.ok) {
        const data = await response.json();
        const shopsArray = Array.isArray(data) ? data : data.results || data.shops || [];
        setShops(shopsArray);
        const ukShop = findUkShop(shopsArray);
        if (ukShop) {
          setUkShopId(ukShop.shop_id);
          setShopName(ukShop.shop_name);
          if (!shopId || shopId === 'all') {
            navigate(`/shop/${ukShop.shop_id}/payments`, { replace: true });
            setIsUkShopSet(true);
          }
        }
        return shopsArray;
      }
      return [];
    } catch (error) {
      console.error('Error fetching shops:', error);
      return [];
    }
  };

  const fetchOrdersForShop = async (shopIdValue) => {
    setFetchingOrders(true);
    try {
      let url = `${API_BASE_URL}/order/getAllorder/`;
      if (shopIdValue && shopIdValue !== 'all' && shopIdValue !== '') {
        url += `?shop_id=${shopIdValue}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        let ordersData = [];
        if (data && data.orders && Array.isArray(data.orders)) ordersData = data.orders;
        else if (Array.isArray(data)) ordersData = data;
        else if (data.results) ordersData = data.results;

        const formattedOrders = ordersData.map(order => ({
          id: order.order_Id,
          trackingId: order.order_Tracking_Id || `ORD-${order.order_Id}`,
          display: `${order.order_Tracking_Id || `ORD-${order.order_Id}`} - ${order.customer?.first_Name || ''} ${order.customer?.last_Name || ''} - ${formatPrice(order.total_Amount || 0)}`,
          customerName: `${order.customer?.first_Name || ''} ${order.customer?.last_Name || ''}`.trim(),
          total: order.total_Amount || 0,
          shop_id: order.shop_id || order.shop?.shop_id,
          shop_name: order.shop_name || order.shop?.shop_name,
          city: order.city || '',
          subtotal: parseFloat(order.subtotal) || 0,
          vat_amount: parseFloat(order.vat_amount) || 0,
          delivery_charges: parseFloat(order.delivery_Charges || order.delivery_charges) || 0,
          discount: parseFloat(order.discount) || 0,
          apply_vat: order.apply_vat || false,
          apply_delivery_charges: order.apply_delivery_charges || false
        }));
        setOrderOptions(formattedOrders);
      } else {
        setOrderOptions([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrderOptions([]);
    } finally {
      setFetchingOrders(false);
    }
  };

  // Loads the payments of one shop. Runs in the background: it never disables
  // the buttons, and only the latest request is allowed to update the screen.
  const loadPaymentsFromBackend = async (targetShopId) => {
    const requestId = ++paymentsRequestRef.current;
    setError(null);
    try {
      let url = `${API_BASE_URL}/order/api/payments/all/`;
      if (targetShopId) {
        url += `?shop_id=${targetShopId}`;
      }

      const response = await fetch(url);
      if (requestId !== paymentsRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (requestId !== paymentsRequestRef.current) return;

        const allPayments = data.map(item => {
          const amountValue = extractNumericValue(item.paymentAmount);
          const paidValue = extractNumericValue(item.paymentPaid);
          return {
            id: item.id,
            paymentStatus: item.paymentStatus || item.payment_status || 'Pending',
            paymentAmountRaw: amountValue,
            paymentPaidRaw: paidValue,
            paymentAmount: item.paymentAmount || '0.00',
            paymentPaid: item.paymentPaid || '0.00',
            order: item.order || 'N/A',
            order_id: item.order_id || null,
            shop_id: item.shop_id,
            shop_name: item.shop_name || item.shop?.shop_name || 'Unknown Shop',
            type: item.type || 'payment',
            created_at: item.created_at || new Date().toISOString(),
            city: item.city || '',
            subtotal: parseFloat(item.subtotal) || 0,
            vat_amount: parseFloat(item.vat_amount) || 0,
            delivery_charges: parseFloat(item.delivery_charges) || 0,
            discount: parseFloat(item.discount) || 0,
            order_total: parseFloat(item.order_total) || 0,
            apply_vat: item.apply_vat || false,
            apply_delivery_charges: item.apply_delivery_charges || false
          };
        });

        setPayments(allPayments);

        const storageKey = targetShopId ? paymentsKey(targetShopId) : 'payments_all';
        writeJson(storageKey, allPayments);
      } else {
        // keep what is already shown (do not wipe the saved list on a server error)
        setError(`Failed to fetch payments: ${response.status}`);
      }
    } catch (error) {
      if (requestId !== paymentsRequestRef.current) return;
      console.error('Error loading payments:', error);
      setError(error.message);
      const storageKey = targetShopId ? paymentsKey(targetShopId) : 'payments_all';
      const savedPayments = readJson(storageKey);
      if (Array.isArray(savedPayments)) {
        setPayments(savedPayments);
      }
    } finally {
      if (requestId === paymentsRequestRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (shopId && shopId !== 'all') {
        // Shop changed: show the saved list of this shop immediately (if there is one)
        if (prevShopRef.current !== shopId) {
          prevShopRef.current = shopId;
          const cachedPayments = readJson(paymentsKey(shopId));
          const cachedCities = readJson(citiesKey(shopId));
          if (Array.isArray(cachedPayments)) {
            setPayments(cachedPayments);
            setIsLoading(false);
          } else {
            setPayments([]);
            setIsLoading(true);
          }
          const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
          setFilterCities(savedCities);
          setCities(savedCities);
          setSelectedFilterCity('');
        }

        setSelectedShopForPayment(shopId.toString());

        // Everything loads together instead of one after another
        const tasks = [loadPaymentsFromBackend(shopId)];

        if (citiesLoadedForRef.current !== shopId) {
          citiesLoadedForRef.current = shopId;
          tasks.push(fetchCitiesForShop(shopId));
          tasks.push(fetchFilterCities(shopId));
        }

        if (!shopsFetchedRef.current) {
          shopsFetchedRef.current = true;
          tasks.push(fetchShops());
        }

        await Promise.all(tasks);
      } else {
        // No shop in the URL: fetchShops() redirects to the default shop,
        // and this effect runs again with that shop.
        shopsFetchedRef.current = true;
        const shopsData = await fetchShops();
        if (!shopsData || shopsData.length === 0) {
          setIsLoading(false);
        }
      }
    };
    initialize();
  }, [shopId, refreshKey]);

  useEffect(() => {
    if (isModalOpen) {
      let targetShopId = null;
      if (editingPayment) targetShopId = editingPayment.shop_id;
      else if (selectedShopForPayment) targetShopId = selectedShopForPayment;
      else if (shopId && shopId !== 'all') targetShopId = shopId;

      if (targetShopId) {
        fetchOrdersForShop(targetShopId);
        fetchCitiesForShop(targetShopId);
      }
    }
  }, [isModalOpen, editingPayment, selectedShopForPayment, shopId]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        orderDropdownRef.current &&
        !orderDropdownRef.current.contains(event.target) &&
        orderInputRef.current &&
        !orderInputRef.current.contains(event.target)
      ) {
        setShowOrderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // =========================
  // SELECTION LOGIC
  // =========================
  const handleSelectPayment = paymentId => {
    setSelectedPayments(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPayments.length === filteredPayments.length && filteredPayments.length > 0) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredPayments.map(payment => payment.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedPayments([]);
    setShowActionDropdown(false);
  };

  // =========================
  // MODAL HANDLERS
  // =========================
  const resetOrderFields = () => {
    setSubtotal('');
    setVatAmount('');
    setDeliveryCharges('');
    setDiscount('');
    setOrderTotal('');
    setApplyVat(false);
    setApplyDeliveryCharges(false);
  };

  const handleOpenModal = () => {
    setEditingPayment(null);
    setPaymentStatus('');
    setPaymentAmount('');
    setPaymentPaid('');
    setOrder('');
    setOrderId('');
    setSelectedOrderTrackingId('');
    resetOrderFields();
    if (shopId && shopId !== 'all') {
      setSelectedShopForPayment(shopId.toString());
    }
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = payment => {
    setEditingPayment(payment);
    setPaymentStatus(payment.paymentStatus || '');
    setPaymentAmount(payment.paymentAmountRaw?.toString() || '');
    setPaymentPaid(payment.paymentPaidRaw?.toString() || '');
    setOrder(payment.order || '');
    setOrderId(payment.order_id || '');
    setSelectedOrderTrackingId(payment.order || '');

    // ✅ NEW: Populate order fields for edit
    setSubtotal(payment.subtotal ? String(payment.subtotal) : '');
    setVatAmount(payment.vat_amount ? String(payment.vat_amount) : '');
    setDeliveryCharges(payment.delivery_charges ? String(payment.delivery_charges) : '');
    setDiscount(payment.discount ? String(payment.discount) : '');
    setOrderTotal(payment.order_total ? String(payment.order_total) : '');
    setApplyVat(!!payment.apply_vat);
    setApplyDeliveryCharges(!!payment.apply_delivery_charges);

    if (payment.shop_id) {
      setSelectedShopForPayment(payment.shop_id.toString());
    }
    setSelectedCity(payment.city || '');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (loading) return;
    setIsModalOpen(false);
    setEditingPayment(null);
    setPaymentStatus('');
    setPaymentAmount('');
    setPaymentPaid('');
    setOrder('');
    setOrderId('');
    setSelectedOrderTrackingId('');
    resetOrderFields();
    setShowOrderDropdown(false);
  };

  const handleShopForPaymentChange = e => {
    const value = e.target.value;
    setSelectedShopForPayment(value);
    setOrder('');
    setOrderId('');
    setSelectedOrderTrackingId('');
    resetOrderFields();
    if (value) {
      fetchCitiesForShop(value);
      fetchOrdersForShop(value);
    } else {
      setCities([]);
      setSelectedCity('');
      setOrderOptions([]);
    }
  };

  const handleCityChange = e => setSelectedCity(e.target.value);
  const handlePaymentStatusChange = e => setPaymentStatus(e.target.value);
  const handlePaymentAmountChange = e => setPaymentAmount(e.target.value);
  const handlePaymentPaidChange = e => setPaymentPaid(e.target.value);
  const handleOrderChange = e => setOrder(e.target.value);

  // ✅ FIXED: Auto-calculate total (renamed inner var to avoid 'disc' collision)
  const handleSubtotalChange = (e) => {
    const val = e.target.value;
    setSubtotal(val);
    recalcOrderTotal(val, vatAmount, deliveryCharges, discount);
  };

  const handleVatChange = (e) => {
    const val = e.target.value;
    setVatAmount(val);
    recalcOrderTotal(subtotal, val, deliveryCharges, discount);
  };

  const handleDeliveryChange = (e) => {
    const val = e.target.value;
    setDeliveryCharges(val);
    recalcOrderTotal(subtotal, vatAmount, val, discount);
  };


  const handleDiscountChange = (e) => {
    const val = e.target.value;
    setDiscount(val);
    recalcOrderTotal(subtotal, vatAmount, deliveryCharges, val);
  };

  const handleApplyVatChange = (e) => {
    const checked = e.target.checked;
    setApplyVat(checked);
    recalcOrderTotal(subtotal, vatAmount, deliveryCharges, discount, checked, applyDeliveryCharges);
  };

  const handleApplyDeliveryChange = (e) => {
    const checked = e.target.checked;
    setApplyDeliveryCharges(checked);
    recalcOrderTotal(subtotal, vatAmount, deliveryCharges, discount, applyVat, checked);
  };

  // ✅ FIXED: No duplicate 'disc' declaration
  const recalcOrderTotal = (sub, vat, del, disc, applyV = applyVat, applyD = applyDeliveryCharges) => {
    const s = parseFloat(sub) || 0;
    const v = applyV ? (parseFloat(vat) || 0) : 0;
    const d = applyD ? (parseFloat(del) || 0) : 0;
    const discountVal = parseFloat(disc) || 0;
    const total = s + v + d - discountVal;
    setOrderTotal(String(Math.max(0, total).toFixed(2)));
  };

  const handleOrderClick = () => {
    if (!selectedShopForPayment) {
      showWarningAlert('Please select a shop first.');
      return;
    }
    if (orderInputRef.current && !showOrderDropdown) {
      const rect = orderInputRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.top, left: rect.left });
    }
    setShowOrderDropdown(prev => !prev);
  };

  const handleOrderSelect = orderItem => {
    setOrder(orderItem.trackingId);
    setOrderId(orderItem.id);
    setSelectedOrderTrackingId(orderItem.trackingId);
    setShowOrderDropdown(false);

    // ✅ NEW: Populate order financial fields when selecting an order
    setSubtotal(orderItem.subtotal ? String(orderItem.subtotal) : '');
    setVatAmount(orderItem.vat_amount ? String(orderItem.vat_amount) : '');
    setDeliveryCharges(orderItem.delivery_charges ? String(orderItem.delivery_charges) : '');
    setDiscount(orderItem.discount ? String(orderItem.discount) : '');
    setOrderTotal(orderItem.total ? String(orderItem.total) : '');
    setApplyVat(!!orderItem.apply_vat);
    setApplyDeliveryCharges(!!orderItem.apply_delivery_charges);
  };

  // ✅ UPDATED: Save handler now correctly persists payment + order changes to backend
  const handleSave = async () => {
    if (!paymentStatus) { showWarningAlert('Please select payment status.'); return; }
    if (!paymentAmount) { showWarningAlert('Please enter payment amount.'); return; }
    if (!paymentPaid) { showWarningAlert('Please enter paid amount.'); return; }
    if (!selectedShopForPayment) { showWarningAlert('Please select a shop.'); return; }

    setLoading(true);
    try {
      if (editingPayment) {
        // ============================
        // UPDATE existing payment + order (all-in-one endpoint)
        // ============================
        const payload = {
          paymentStatus,
          paymentAmount,
          paymentPaid,
          order: orderId || order,
          shop_id: selectedShopForPayment,
          city: selectedCity,
          // order financials
          subtotal: parseFloat(subtotal) || 0,
          vat_amount: applyVat ? (parseFloat(vatAmount) || 0) : 0,
          delivery_charges: applyDeliveryCharges ? (parseFloat(deliveryCharges) || 0) : 0,
          delivery_Charges: applyDeliveryCharges ? (parseFloat(deliveryCharges) || 0) : 0,
          discount: parseFloat(discount) || 0,
          total_Amount: parseFloat(orderTotal) || 0,
          apply_vat: applyVat,
          apply_delivery_charges: applyDeliveryCharges
        };

        const url = `${API_BASE_URL}/order/api/payments/${editingPayment.id}/`;

        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorMessage = 'Failed to update payment.';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.detail || errorMessage;
          } catch (err) {}
          throw new Error(errorMessage);
        }

        showSuccessAlert('Payment updated successfully!');
      } else {
        // ============================
        // CREATE new payment
        // ============================
        const payload = {
          paymentStatus,
          paymentAmount,
          paymentPaid,
          order: orderId || order,
          shop_id: selectedShopForPayment,
          city: selectedCity
        };

        const response = await fetch(`${API_BASE_URL}/order/api/payments/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorMessage = 'Failed to save payment.';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.detail || errorMessage;
          } catch (err) {}
          throw new Error(errorMessage);
        }

        showSuccessAlert('Payment added successfully!');
      }

      handleCloseModal();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving payment:', error);
      showErrorAlert(error.message || 'Failed to save payment.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingPayment) return;
    const result = await showDeleteConfirmation(editingPayment.order || editingPayment.id);
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/order/api/payments/${editingPayment.id}/`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete payment.');
      showSuccessAlert('Payment deleted successfully!');
      handleCloseModal();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting payment:', error);
      showErrorAlert(error.message || 'Failed to delete payment.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSingle = async (payment) => {
    const paymentLabel = payment.order || payment.id;
    const result = await Swal.fire({
      title: 'Delete Payment?',
      html: `Are you sure you want to delete payment for <strong>${paymentLabel}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/order/api/payments/${payment.id}/`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete payment.');

      showSuccessAlert('Payment deleted successfully!');
      setSelectedPayments(prev => prev.filter(id => id !== payment.id));
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting payment:', error);
      showErrorAlert(error.message || 'Failed to delete payment.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filter by BOTH search term AND selected city
  const filteredPayments = payments.filter(payment => {
    if (selectedFilterCity && selectedFilterCity.trim() !== '') {
      if (payment.city !== selectedFilterCity) {
        return false;
      }
    }

    const search = searchTerm.toLowerCase();
    const matchesSearch =
      !search ||
      String(payment.order || '').toLowerCase().includes(search) ||
      String(payment.paymentStatus || '').toLowerCase().includes(search) ||
      String(payment.shop_name || '').toLowerCase().includes(search) ||
      String(payment.city || '').toLowerCase().includes(search);

    return matchesSearch;
  });

  const isOperationInProgress = loading;
  const currencySymbol = getCurrencySymbol ? getCurrencySymbol() : currency;

  return (
    <div className="payments-main-container">
      {/* ✅ HEADER - EXACTLY like OrderstatusImage */}
      <div className="payments-header-section">
        <div className="payments-header-left">
          <SiAlipay className="payments-header-icon" />
          <h2 className="payments-header-title">Payments Management</h2>
          {/* ✅ City Badge — ALWAYS shown; "All Cities" when no city selected */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredPayments.length})`}
            </span>
          </span>
        </div>

        <div className="payments-header-right">
          {/* ✅ City Dropdown with Count */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || filterCities.length === 0}
            >
              {/* ✅ Default option = All Cities */}
              <option value="">
                {loadingFilterCities ? 'Loading...' : 'All Cities'}
              </option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          {/* ✅ Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Payment Status, Order, Shop or City..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* ✅ Select All */}
          <button
            className="select-all-btn"
            onClick={selectedPayments.length > 0 ? handleDeselectAll : handleSelectAll}
            disabled={isOperationInProgress}
          >
            <FaCheckDouble />
            <span>{selectedPayments.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>

          {/* ✅ Add Payment */}
          <button
            className="add-payment-btn"
            onClick={handleOpenModal}
            disabled={isOperationInProgress}
          >
            <FaPlus />
            <span>ADD PAYMENT</span>
          </button>
        </div>
      </div>

      <div className="payments-content">
        {/* Selection Summary */}
        {selectedPayments.length > 0 && (
          <div className="selection-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedPayments.length} payment(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(prev => !prev)}
                  disabled={isOperationInProgress}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={async () => {
                        const result = await showDeleteConfirmation('', true, selectedPayments.length);
                        if (!result.isConfirmed) return;
                        setLoading(true);
                        try {
                          await Promise.all(
                            selectedPayments.map(id =>
                              fetch(`${API_BASE_URL}/order/api/payments/${id}/`, { method: 'DELETE' })
                            )
                          );
                          showSuccessAlert('Selected payments deleted successfully!');
                          setSelectedPayments([]);
                          setRefreshKey(prev => prev + 1);
                        } catch (error) {
                          console.error(error);
                          showErrorAlert('Failed to delete selected payments.');
                        } finally {
                          setLoading(false);
                          setShowActionDropdown(false);
                        }
                      }}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedPayments.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={handleDeselectAll}>
                Clear all
              </button>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* TABLE */}
        <div className="payments-table-container">
          <div className="payments-table-wrapper">
            <table className="payments-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={filteredPayments.length > 0 && selectedPayments.length === filteredPayments.length}
                      onChange={handleSelectAll}
                      disabled={isOperationInProgress}
                    />
                  </th>
                  <th>Payment Status</th>
                  <th>Order</th>
                  <th>Subtotal ({currencySymbol})</th>
                  <th>VAT ({currencySymbol})</th>
                  <th>Delivery ({currencySymbol})</th>
                  <th>Discount ({currencySymbol})</th>
                  <th>Total ({currencySymbol})</th>
                  <th>Paid ({currencySymbol})</th>
                  <th>City</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length > 0 ? (
                  filteredPayments.map((payment, index) => (
                    <tr
                      key={payment.id || index}
                      className={selectedPayments.includes(payment.id) ? 'selected' : ''}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => handleSelectPayment(payment.id)}
                          disabled={isOperationInProgress}
                        />
                      </td>
                      <td>
                        <span className={`status-badge ${(payment.paymentStatus || '').toLowerCase()}`}>
                          {payment.paymentStatus}
                        </span>
                      </td>
                      <td className="payment-order-cell">{payment.order}</td>
                      <td>{formatPrice(payment.subtotal || 0)}</td>
                      <td>{formatPrice(payment.apply_vat ? (payment.vat_amount || 0) : 0)}</td>
                      <td>{formatPrice(payment.apply_delivery_charges ? (payment.delivery_charges || 0) : 0)}</td>
                      <td>{formatPrice(payment.discount || 0)}</td>
                      <td className="payment-total-cell">{formatPrice(payment.order_total || 0)}</td>
                      <td>{formatPrice(payment.paymentPaidRaw || 0)}</td>
                      <td>{payment.city || '-'}</td>
                      <td className="actions-cell">
                        <div className="actions-cell-inner">
                          <button
                            className="edit-payment-btn"
                            onClick={() => handleOpenModalForEdit(payment)}
                            disabled={isOperationInProgress}
                            title="Edit Payment"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="delete-payment-btn"
                            onClick={() => handleDeleteSingle(payment)}
                            disabled={isOperationInProgress}
                            title="Delete Payment"
                          >
                            <RiDeleteBinFill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="no-data-cell">
                      <FaBuilding className="no-data-icon" />
                      <p>
                        {loading
                          ? 'Loading payments...'
                          : selectedFilterCity
                            ? `No payments found in "${selectedFilterCity}".`
                            : 'No payments found'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL - ✅ UPDATED: Shows order fields in edit mode */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPayment ? 'Edit Payment' : 'Add New Payment'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="payment-form">
                {/* Shop Selector */}
                <div className="form-row">
                  <label className="form-label">SELECT SHOP:</label>
                  <select
                    className="payment-select"
                    value={selectedShopForPayment}
                    onChange={handleShopForPaymentChange}
                    required
                    disabled={isOperationInProgress || editingPayment}
                  >
                    <option value="">Select a shop</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={String(shop.shop_id)}>
                        {shop.shop_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* City */}
                <div className="form-row">
                  <label className="form-label">City:</label>
                  <input
                    type="text"
                    list="payment-city-options"
                    value={selectedCity}
                    onChange={handleCityChange}
                    placeholder="Type or select a city"
                    className="payment-input"
                    disabled={isOperationInProgress || loadingCities}
                  />
                  <datalist id="payment-city-options">
                    {cities.map((city, index) => (
                      <option key={index} value={city} />
                    ))}
                  </datalist>
                </div>

                {/* Status */}
                <div className="form-row">
                  <label className="form-label">Payment Status:</label>
                  <select
                    value={paymentStatus}
                    onChange={handlePaymentStatusChange}
                    className="payment-select"
                    disabled={isOperationInProgress}
                  >
                    <option value="">Select Payment Status</option>
                    {paymentStatusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div className="form-row">
                  <label className="form-label">Payment Amount ({currency}):</label>
                  <input
                    type="text"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    placeholder={`Enter payment amount in ${currency} (e.g., 250.00)`}
                    className="payment-input"
                    disabled={isOperationInProgress}
                  />
                </div>

                {/* Paid */}
                <div className="form-row">
                  <label className="form-label">Payment Paid ({currency}):</label>
                  <input
                    type="text"
                    value={paymentPaid}
                    onChange={handlePaymentPaidChange}
                    placeholder={`Enter paid amount in ${currency} (e.g., 250.00)`}
                    className="payment-input"
                    disabled={isOperationInProgress}
                  />
                </div>

                {/* Order */}
                <div className="form-row">
                  <label className="form-label">Order:</label>
                  <div className="input-with-dropdown">
                    <input
                      ref={orderInputRef}
                      type="text"
                      value={order}
                      onChange={handleOrderChange}
                      onClick={handleOrderClick}
                      placeholder={fetchingOrders ? 'Loading orders...' : 'Select order'}
                      className="payment-input"
                      readOnly
                      disabled={isOperationInProgress || !selectedShopForPayment}
                    />
                    <button
                      type="button"
                      className="dropdown-toggle-btn"
                      onClick={handleOrderClick}
                      disabled={isOperationInProgress || fetchingOrders || !selectedShopForPayment}
                    >
                      <FaChevronDown className={`dropdown-arrow ${showOrderDropdown ? 'rotated' : ''}`} />
                    </button>
                  </div>
                  {!selectedShopForPayment && (
                    <small style={{ color: '#999', marginTop: '5px', display: 'block' }}>Please select a shop first</small>
                  )}
                  {selectedOrderTrackingId && (
                    <small style={{ color: '#28a745', marginTop: '5px', display: 'block' }}>Reference: {selectedOrderTrackingId}</small>
                  )}
                </div>

                {/* ✅ NEW: Order Financial Fields (shown only in Edit mode when an order is selected) */}
                {editingPayment && orderId && (
                  <>
                    <div className="modal-section-title">Order Financial Details</div>

                    <div className="form-row">
                      <label className="form-label">Subtotal ({currency}):</label>
                      <input
                        type="number"
                        step="0.01"
                        value={subtotal}
                        onChange={handleSubtotalChange}
                        className="payment-input"
                        disabled={isOperationInProgress}
                      />
                    </div>

                    <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={applyVat}
                          onChange={handleApplyVatChange}
                          disabled={isOperationInProgress}
                        />
                        Apply VAT
                      </label>
                    </div>

                    {applyVat && (
                      <div className="form-row">
                        <label className="form-label">VAT Amount ({currency}):</label>
                        <input
                          type="number"
                          step="0.01"
                          value={vatAmount}
                          onChange={handleVatChange}
                          className="payment-input"
                          disabled={isOperationInProgress}
                        />
                      </div>
                    )}

                    <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={applyDeliveryCharges}
                          onChange={handleApplyDeliveryChange}
                          disabled={isOperationInProgress}
                        />
                        Apply Delivery Charges
                      </label>
                    </div>

                    {applyDeliveryCharges && (
                      <div className="form-row">
                        <label className="form-label">Delivery Charges ({currency}):</label>
                        <input
                          type="number"
                          step="0.01"
                          value={deliveryCharges}
                          onChange={handleDeliveryChange}
                          className="payment-input"
                          disabled={isOperationInProgress}
                        />
                      </div>
                    )}

                    <div className="form-row">
                      <label className="form-label">Discount ({currency}):</label>
                      <input
                        type="number"
                        step="0.01"
                        value={discount}
                        onChange={handleDiscountChange}
                        className="payment-input"
                        disabled={isOperationInProgress}
                      />
                    </div>

                    <div className="form-row">
                      <label className="form-label">Order Total ({currency}):</label>
                      <input
                        type="number"
                        step="0.01"
                        value={orderTotal}
                        onChange={(e) => setOrderTotal(e.target.value)}
                        className="payment-input"
                        style={{ fontWeight: 'bold', color: '#004aad' }}
                        disabled={isOperationInProgress}
                      />
                    </div>
                  </>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {loading ? 'Saving...' : editingPayment ? 'Update Payment' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ORDER DROPDOWN */}
      {showOrderDropdown && (
        <div
          className="order-dropdown-outside"
          ref={orderDropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top + 40}px`,
            left: `${dropdownPosition.left}px`,
            minWidth: '400px',
            maxWidth: '500px',
            zIndex: 10001
          }}
        >
          <div className="order-dropdown-header">
            <FaShoppingCart />
            <span>{fetchingOrders ? 'Loading orders...' : 'Select Order'}</span>
          </div>
          <div className="order-dropdown-list">
            {fetchingOrders ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>Loading orders...</div>
            ) : orderOptions.length > 0 ? (
              orderOptions.map((orderItem, index) => (
                <button
                  key={index}
                  type="button"
                  className="order-dropdown-item"
                  onClick={() => handleOrderSelect(orderItem)}
                >
                  <FaShoppingCart className="order-icon" />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 'bold' }}>{orderItem.trackingId}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>{orderItem.customerName} - {formatPrice(orderItem.total)}</span>
                    {orderItem.city && (
                      <span style={{ fontSize: '11px', color: '#004aad' }}>
                        <FaCity style={{ marginRight: '3px' }} /> {orderItem.city}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No orders found for this shop</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;